import { useMemo } from 'react';
import type { DimensionMeta } from '@qronstat/shared';
import { useApp } from '../../app/context';
import { useSemanticQuery } from '../../api/hooks';
import { text } from '../../model/meta';

export interface ValueOption {
  value: string;
  label: string;
}

export function useDimensionValues(dim: DimensionMeta | undefined, enabled = true) {
  const { scope, meta, modelVersion, locale } = useApp();
  const counter = useMemo(() => {
    if (!dim || !meta || dim.values) return null;
    const own = meta.meta.metrics.find((m) => m.cube === dim.cube && !m.derived && !m.live);
    if (own) return own.id;
    const fact = meta.meta.facts.find(
      (f) =>
        Object.values(f.objects).length && meta.meta.metrics.some((m) => m.cube === f.cube && !m.derived),
    );
    return fact ? (meta.meta.metrics.find((m) => m.cube === fact.cube && !m.derived)?.id ?? null) : null;
  }, [dim, meta]);
  const query = useMemo(
    () =>
      dim && counter && enabled
        ? {
            metrics: [counter],
            dimensions: [dim.id],
            compare: 'none' as const,
            filters: [],
            realOnly: false,
            timeMode: 'msk' as const,
            order: [{ member: dim.id, direction: 'asc' as const }],
            limit: 2000,
            offset: 0,
            excludeMarks: false,
            ignorePeriod: true,
          }
        : null,
    [dim, counter, enabled],
  );
  const result = useSemanticQuery(scope, modelVersion, query, { keepPrevious: false });
  const options = useMemo<ValueOption[]>(() => {
    if (!dim) return [];
    if (dim.values) {
      const keys = dim.order ?? Object.keys(dim.values);
      return keys.map((k) => ({ value: k, label: dim.values?.[k]?.[locale] ?? k }));
    }
    const seen = new Set<string>();
    const out: ValueOption[] = [];
    for (const r of result.data?.data.rows ?? []) {
      const v = r[dim.id];
      if (v === null || v === undefined || v === '') continue;
      const key = String(v);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ value: key, label: key });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [dim, result.data, locale]);
  return { options, loading: Boolean(query) && result.isPending, title: text(dim?.title, locale) };
}
